import React, { useEffect, useState, useRef } from 'react';
import useAxios from 'axios-hooks';
import { PrimaryButton, Checkbox } from '@fluentui/react';
import { Form, Formik } from 'formik';
import { useParams, useHistory } from 'react-router-dom';
import Select from 'react-select';
import { FixedSizeList as List } from 'react-window';
import TextField from '../../../../Forms/TextField';
import Toast from '../../../../Forms/Toast';
import ErrorComponent from '../../../../Util/ErrorComponent';
import FeatureSummary from '../../../../Util/FeatureSummary';
import { BlobServiceClient } from '@azure/storage-blob';
import { Buffer } from 'buffer';
import env from '../../../../../env';

window.Buffer = Buffer;

const CustomMenuList = ({ children }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        padding: '0 8px',
                        boxSizing: 'border-box',
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

const newCoBrandSettingsObject = {
    coBrandId: null,
    themedMonthlyTaskOverrideId: null,
    year: '',
    month: '',
    themeName: '',
    international: false,
    articleSlug1: '',
    articleSlug2: '',
    videoSlug: '',
    infographicSlug: '',
    courseSlug: '',
    toolId1: '',
    toolId2: '',
};

const WizardPage = () => {
    const [showToast, setShowToast] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [uploadedImageName, setUploadedImageName] = useState(null);
    const [imageError, setImageError] = useState(false);
    const [imageVersion, setImageVersion] = useState(null);

    const history = useHistory();
    const { themedMonthlyTaskOverrideId: idFromParams } = useParams();

    const fileInputRef = useRef(null);
    const monthNamesShort = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

    const [{ data: domain }, getDisclaimer] = useAxios(
        { url: `/internal/cobrand/alternate-monthly-task/${idFromParams}`, method: 'GET' },
        { manual: true }
    );

    const [{ data: cobrands }] = useAxios('/internal/cobrands');
    const cobrandsOptions = cobrands?.map(u => ({ value: u.cobrandId, label: u.name })) || [];

    const [{}, submitForm] = useAxios(
        { url: `/internal/cobrand/alternate-monthly-task/update` },
        { manual: true }
    );

    useEffect(() => {
        if (idFromParams) getDisclaimer();
    }, [idFromParams]);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageUpload = async (event, values) => {
        const file = event.target.files[0];
        if (!file || !values.coBrandId || !values.month) return;

        const monthIndex = parseInt(values.month) - 1;
        const monthName = monthNamesShort[monthIndex] || 'unknown';
        const cobrandName = cobrandsOptions.find(opt => opt.value === values.coBrandId)?.label.replace(/\s+/g, '') || 'unknown';
        const imageName = `icon-${monthName}-custom${cobrandName}.png`.toLowerCase();

        const blobUrl = 'https://igradcdnproduction.blob.core.windows.net/';
        const containerName = 'images';
        const folderName = 'dashboard/icons/';
        const blobServiceClient = new BlobServiceClient(`${blobUrl}${env.REACT_APP_SAS_TOKEN}`);
        const containerClient = blobServiceClient.getContainerClient(containerName);
        const blockBlobClient = containerClient.getBlockBlobClient(`${folderName}${imageName}`);

        try {
            await blockBlobClient.uploadData(file, {
                blobHTTPHeaders: { blobContentType: file.type },
            });
            setUploadedImageName(imageName);
            setImageVersion(Date.now());
            setImageError(false);
        } catch (error) {
            console.error('Upload failed:', error.message);
            alert('Upload failed. Please try again.');
        }
    };

    return (
        <Formik
            initialValues={domain || newCoBrandSettingsObject}
            enableReinitialize
            validateOnBlur
            isInitialValid={false}
            validate={values => {
                const errors = {};
                if (!values.themeName) errors.themeName = 'Theme Name is required';
                if (!values.year) errors.year = 'Year is required';
                if (!values.month) errors.month = 'Month is required';
                return errors;
            }}
            onSubmit={async (values) => {
                setShowError(false);
                setErrorMessage('');
                const cleanedValues = Object.fromEntries(
                    Object.entries(values).map(([key, value]) => [key, value === '' ? null : value])
                );

                const result = await submitForm({
                    url: idFromParams
                        ? '/internal/cobrand/alternate-monthly-task/update'
                        : '/internal/cobrand/alternate-monthly-task/create',
                    method: 'POST',
                    data: cleanedValues,
                });

                if (!result.isAxiosError) {
                    if (!idFromParams) {
                        history.push(`/cobrand-settings-feature/alternate-monthly-tasks/${result.data}/wizard`);
                    }
                    setShowToast(true);
                } else {
                    setShowError(true);
                    setErrorMessage(result?.response?.data?.message || 'An unexpected error occurred.');
                }
            }}
        >
            {({ values, errors, setFieldValue, handleSubmit }) => {
                const getInitialImageName = () => {
                    if (!values.coBrandId || !values.month || !Array.isArray(cobrandsOptions)) return null;
                    const monthIndex = parseInt(values.month) - 1;
                    const monthName = monthNamesShort[monthIndex];
                    const cobrandName = cobrandsOptions.find(opt => opt.value === values.coBrandId)?.label.replace(/\s+/g, '');
                    if (!monthName || !cobrandName) return null;
                    return `icon-${monthName}-custom${cobrandName}.png`.toLowerCase();
                };

                const imageNameToShow = uploadedImageName || getInitialImageName();
                const imageUrl = imageNameToShow
                    ? `https://cdn.igrad.com/images/dashboard/icons/${imageNameToShow}${imageVersion ? `?t=${imageVersion}` : ''}`
                    : null;

                useEffect(() => {
                    setImageError(false);
                }, [values.coBrandId, values.month]);

                return (
                    <Form style={{ maxWidth: '500px', marginBottom: 500 }}>
                        <h4>Edit Monthly Task</h4>
                        {showError && (
                            <ErrorComponent
                                errorMessage={errorMessage}
                                showError={showError}
                                setShowError={setShowError}
                                isMultiline
                            />
                        )}

                        <label className="default-label mt20">Select Cobrand</label>
                        <Select
                            components={{ MenuList: CustomMenuList }}
                            options={cobrandsOptions}
                            placeholder="Select Cobrand"
                            name="coBrandId"
                            value={cobrandsOptions.find(option => option.value === values.coBrandId)}
                            onChange={option => setFieldValue('coBrandId', option.value)}
                            isSearchable
                            styles={{
                                menuList: base => ({ ...base, maxHeight: '100%', overflowY: 'auto' }),
                                container: base => ({ ...base, width: '100%' }),
                                control: base => ({ ...base, border: '1px solid #aaaaaa' }),
                            }}
                        />

                        <TextField label="Year" name="year" type="number" value={values.year} onChange={(e, v) => setFieldValue('year', v)} errorMessage={errors.year} />
                        <TextField className="mt5" label="Month" name="month" type="number" value={values.month} onChange={(e, v) => setFieldValue('month', v)} errorMessage={errors.month} />
                        <TextField className="mt5" label="Theme Name" name="themeName" value={values.themeName} onChange={(e, v) => setFieldValue('themeName', v)} errorMessage={errors.themeName} />
                        <Checkbox className="mt10" label="International" checked={values.international} onChange={(e, v) => setFieldValue('international', v)} />
                        <TextField className="mt5" label="Article Slug 1" name="articleSlug1" value={values.articleSlug1} onChange={(e, v) => setFieldValue('articleSlug1', v)} />
                        <TextField className="mt5" label="Article Slug 2" name="articleSlug2" value={values.articleSlug2} onChange={(e, v) => setFieldValue('articleSlug2', v)} />
                        <TextField className="mt5" label="Video Slug" name="videoSlug" value={values.videoSlug} onChange={(e, v) => setFieldValue('videoSlug', v)} />
                        <TextField className="mt5" label="Infographic Slug" name="infographicSlug" value={values.infographicSlug} onChange={(e, v) => setFieldValue('infographicSlug', v)} />
                        <TextField className="mt5" label="Course Slug" name="courseSlug" value={values.courseSlug} onChange={(e, v) => setFieldValue('courseSlug', v)} />
                        <TextField className="mt5" label="Tool ID 1" name="toolId1" type="number" value={values.toolId1} onChange={(e, v) => setFieldValue('toolId1', v)} />
                        <TextField className="mt5" label="Tool ID 2" name="toolId2" type="number" value={values.toolId2} onChange={(e, v) => setFieldValue('toolId2', v)} />

                        <div className="mt20">
                            <label className="default-label">Upload Icon Image</label>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={e => handleImageUpload(e, values)}
                            />
                            <PrimaryButton className="mt5" text="Upload Image" onClick={handleUploadClick} />

                            {imageUrl && !imageError && (
                                <div className="mt10">
                                    <img
                                        src={imageUrl}
                                        style={{ width: 30 }}
                                        alt="Uploaded Icon"
                                        onError={() => setImageError(true)}
                                        onLoad={() => setImageError(false)}
                                    />
                                </div>
                            )}
                        </div>

                        <PrimaryButton
                            className="mt20"
                            onClick={handleSubmit}
                            text={idFromParams ? 'Update' : 'Create'}
                            disabled={!values.themeName || !values.year || !values.month}
                        />

                        <Toast show={showToast} message="Saved!" onClose={() => setShowToast(false)} />
                        <FeatureSummary
                            label="About Alternate Monthly Tasks"
                            summary="Create cobrand-specific monthly task overrides. Configure year, month, theme name, and specify content slugs for articles, videos, infographics, courses, and tools that differ from the global monthly tasks. Upload a custom icon image for the month's theme."
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

export default WizardPage;
